import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { useCustomerStore } from '../../store/useCustomerStore.ts';
import { useObjectStore } from '../../store/useObjectStore.ts';
import { useContactStore } from '../../store/useContactStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Badge } from '../../components/Shared/Badge.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { SlideOver, SlideOverFooter } from '../../components/Shared/SlideOver.tsx';
import { Modal, ModalFooter } from '../../components/Shared/Modal.tsx';
import { FormField } from '../../components/Shared/FormField.tsx';
import { SalutationSelect } from '../../components/Shared/SalutationSelect.tsx';
import { formatPersonName, toSalutation, type Salutation } from '../../utils/salutation.ts';
import { TextArea } from '../../components/Shared/TextArea.tsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/Shared/Tabs.tsx';
import { EmptyState } from '../../components/Shared/EmptyState.tsx';
import { DraggableObjectsTable } from './DraggableObjectsTable.tsx';
import { DocumentList } from '../../components/Documents/DocumentList.tsx';
import ObjectEditSlideOver from '../Objects/components/ObjectEditSlideOver';
import {
  ArrowLeft,
  Building2,
  Users,
  FileText,
  Plus,
  Pencil,
  Trash2,
  MapPin,
  Mail,
  Phone,
  Calendar,
  Save,
} from 'lucide-react';
import type { FacilityFlowCoreModulesCustomersDtosObjectDto, FacilityFlowCoreModulesCustomersDtosContactDto } from '../../api/generated/models';
import type { FacilityFlowCoreModulesCustomersDtosCreateObjectRequest } from '../../api/generated/models';
import type { UpdateObjectRequestWithGeo } from '../../types/objects-api';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['customers', 'common']);
  const { selectedCustomer, loading: customerLoading, error: customerError, getCustomerById, updateCustomer } = useCustomerStore();
  const {
    createObject,
    updateObject,
    deleteObject,
    reorderObjects,
    saving: objectSaving,
    getObjectById,
    selectedObject: objectStoreSelectedObject,
  } = useObjectStore();
  const {
    createContact,
    updateContact,
    deleteContact,
    saving: contactSaving,
  } = useContactStore();

  // Use selectedCustomer from store instead of searching in customers array
  const customer = selectedCustomer;
  
  // Get objects and contacts from customer (now included in the detail DTO)
  // Sort objects by displayOrder (API should return them sorted, but we ensure it here)
  const objects: FacilityFlowCoreModulesCustomersDtosObjectDto[] = (customer?.objects || []).sort(
    (a: FacilityFlowCoreModulesCustomersDtosObjectDto, b: FacilityFlowCoreModulesCustomersDtosObjectDto) =>
      (a.displayOrder || 0) - (b.displayOrder || 0)
  );
  const contacts: FacilityFlowCoreModulesCustomersDtosContactDto[] = customer?.contacts || [];

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [selectedContact, setSelectedContact] = useState<FacilityFlowCoreModulesCustomersDtosContactDto | null>(null);

  const [activeTab, setActiveTab] = useState('objects');

  // Object delete confirmation
  const [isDeleteObjectModalOpen, setIsDeleteObjectModalOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState<string | null>(null);

  // Contact delete confirmation
  const [isDeleteContactModalOpen, setIsDeleteContactModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);

  // Unified object create/edit SlideOver
  const [isObjectEditOpen, setIsObjectEditOpen] = useState(false);
  const [objectEditId, setObjectEditId] = useState<string | null>(null);
  const [objectEditMode, setObjectEditMode] = useState<'create' | 'edit'>('create');

  const [editedExternalNotes, setEditedExternalNotes] = useState(customer?.externalNotes || '');
  const [editedInternalNotes, setEditedInternalNotes] = useState(customer?.internalNotes || '');
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  const [contactFormData, setContactFormData] = useState({
    salutation: null as Salutation | null,
    firstName: '',
    lastName: '',
    emails: [''],
    phoneNumbers: [''],
    role: '',
    dateOfBirth: '',
    notes: '',
  });

  useEffect(() => {
    if (id) {
      getCustomerById(id);
      // Objects and Contacts are now included in CustomerDto
      // No need for separate API calls
    }
  }, [id, getCustomerById]);

  // Initialize notes when customer loads
  useEffect(() => {
    if (customer) {
      setEditedExternalNotes(customer.externalNotes || '');
      setEditedInternalNotes(customer.internalNotes || '');
    }
  }, [customer]);

  const handleEditNotes = () => {
    setIsEditingNotes(true);
  };

  const handleCancelEditNotes = () => {
    setIsEditingNotes(false);
    setEditedExternalNotes(customer?.externalNotes || '');
    setEditedInternalNotes(customer?.internalNotes || '');
  };

  const handleSaveNotes = async () => {
    // why: UpdateCustomerRequest requires name + billingAddress. This handler only edits the
    // notes, so it echoes them back unchanged — bail out rather than send a partial payload.
    if (!customer?.id || !customer.name || !customer.billingAddress) return;

    try {
      await updateCustomer(customer.id, {
        name: customer.name,
        billingAddress: customer.billingAddress,
        externalNotes: editedExternalNotes,
        internalNotes: editedInternalNotes,
      });
      setIsEditingNotes(false);
      // Reload customer data
      await getCustomerById(customer.id);
    } catch (error) {
      console.error('Failed to update notes:', error);
    }
  };

  const resetContactForm = () => {
    setContactFormData({
      salutation: null,
      firstName: '',
      lastName: '',
      emails: [''],
      phoneNumbers: [''],
      role: '',
      dateOfBirth: '',
      notes: '',
    });
  };

  const handleCreateContact = async () => {
    if (!id) return;
    try {
      await createContact({
        customerId: id,
        salutation: contactFormData.salutation,
        firstName: contactFormData.firstName,
        lastName: contactFormData.lastName,
        emails: contactFormData.emails.filter((e) => e.trim() !== ''),
        phoneNumbers: contactFormData.phoneNumbers.filter((p) => p.trim() !== ''),
        role: contactFormData.role,
        dateOfBirth: contactFormData.dateOfBirth || undefined,
        notes: contactFormData.notes,
      });
      // Close modal and reset form FIRST
      resetContactForm();
      setIsContactModalOpen(false);
      // Then reload customer data in background
      await getCustomerById(id);
    } catch (error) {
      console.error('Failed to create contact:', error);
    }
  };

  const handleEditContact = async () => {
    if (!selectedContact?.id || !id) return;
    try {
      await updateContact(selectedContact.id, {
        salutation: contactFormData.salutation,
        firstName: contactFormData.firstName,
        lastName: contactFormData.lastName,
        emails: contactFormData.emails.filter((e) => e.trim() !== ''),
        phoneNumbers: contactFormData.phoneNumbers.filter((p) => p.trim() !== ''),
        role: contactFormData.role,
        dateOfBirth: contactFormData.dateOfBirth || undefined,
        notes: contactFormData.notes,
      });
      // Close modal and reset form FIRST
      resetContactForm();
      setSelectedContact(null);
      setIsEditingContact(false);
      setIsContactModalOpen(false);
      // Then reload customer data in background
      await getCustomerById(id);
    } catch (error) {
      console.error('Failed to update contact:', error);
    }
  };

  const openDeleteObjectModal = (objectId: string) => {
    setObjectToDelete(objectId);
    setIsDeleteObjectModalOpen(true);
  };

  const handleDeleteObject = async () => {
    if (!objectToDelete || !id) return;

    try {
      await deleteObject(objectToDelete);
      setIsDeleteObjectModalOpen(false);
      setObjectToDelete(null);
      await getCustomerById(id);
    } catch (error) {
      console.error('Failed to delete object:', error);
    }
  };

  const handleReorderObjects = async (reorderedObjects: FacilityFlowCoreModulesCustomersDtosObjectDto[]) => {
    if (!id) return;

    try {
      await reorderObjects(id, {
        objects: reorderedObjects
          .filter((obj) => Boolean(obj.id))
          .map((obj, index) => ({
            objectId: obj.id!,
            // why: displayOrder is required; fall back to the new array position, which is
            // exactly what a reorder means.
            displayOrder: obj.displayOrder ?? index,
          })),
      });
      await getCustomerById(id);
    } catch (error) {
      console.error('Failed to reorder objects:', error);
      await getCustomerById(id);
    }
  };

  const openDeleteContactModal = (contactId: string) => {
    setContactToDelete(contactId);
    setIsDeleteContactModalOpen(true);
  };

  const handleConfirmDeleteContact = async () => {
    if (!contactToDelete || !id) return;

    try {
      await deleteContact(contactToDelete);
      setIsDeleteContactModalOpen(false);
      setContactToDelete(null);
      await getCustomerById(id);
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const openObjectModal = async (object?: FacilityFlowCoreModulesCustomersDtosObjectDto) => {
    if (object?.id) {
      setObjectEditMode('edit');
      setObjectEditId(object.id);
      await getObjectById(object.id);
      setIsObjectEditOpen(true);
      return;
    }

    setObjectEditMode('create');
    setObjectEditId(null);
    setIsObjectEditOpen(true);
  };

  const openContactModal = (contact?: FacilityFlowCoreModulesCustomersDtosContactDto) => {
    if (contact) {
      setIsEditingContact(true);
      setSelectedContact(contact);
      setContactFormData({
        salutation: toSalutation(contact.salutation),
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        emails: contact.emails && contact.emails.length > 0 ? contact.emails : [''],
        phoneNumbers: contact.phoneNumbers && contact.phoneNumbers.length > 0 ? contact.phoneNumbers : [''],
        role: contact.role || '',
        dateOfBirth: contact.dateOfBirth || '',
        notes: contact.notes || '',
      });
    } else {
      setIsEditingContact(false);
      resetContactForm();
    }
    setActiveTab('contacts'); // Ensure contacts tab is active
    setIsContactModalOpen(true);
  };

  if (customerLoading) return <Loading />;

  if (customerError || !customer) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/customers')}
          leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
        >
          {t('common:actions.back')}
        </Button>
        <Card>
          <EmptyState
            variant="error"
            title={t('customers:messages.loadError')}
            description={customerError || t('common:errors.tryAgain')}
            onRetry={() => id && getCustomerById(id)}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate('/customers')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('common:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
              {customer.name}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('customers:customerNumber')}: <span className="font-mono-num text-slate-700">{customer.customerNumber}</span>
            </p>
          </div>
          <Badge variant={customer.status === 'active' ? 'success' : 'default'} className="flex-shrink-0">
            {customer.status === 'active' ? t('common:status.active') : t('common:status.inactive')}
          </Badge>
        </div>
      </div>

      {/* Stammdaten + Notes */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {customer.billingAddress && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{t('customers:billingAddress')}</p>
                <p className="text-[13px] text-slate-700 mt-0.5">
                  {customer.billingAddress.street}
                  <br />
                  {customer.billingAddress.postalCode} {customer.billingAddress.city}
                  {customer.billingAddress.country && (
                    <>
                      <br />
                      {customer.billingAddress.country}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          {customer.vatId && (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {t('customers:fields.vatId')}
              </p>
              <p className="text-[13px] text-slate-700 font-mono-num mt-0.5">
                {customer.vatId}
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-semibold text-slate-900">
              {t('customers:notes.title')}
            </h3>
            {!isEditingNotes && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditNotes}
                leftIcon={<Pencil className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                {t('customers:notes.edit')}
              </Button>
            )}
          </div>

          {isEditingNotes ? (
            <div className="space-y-4">
              <TextArea
                label={t('customers:notes.external')}
                value={editedExternalNotes}
                onChange={(e) => setEditedExternalNotes(e.target.value)}
                rows={3}
                placeholder={t('customers:placeholders.externalNotes')}
              />
              <TextArea
                label={t('customers:notes.internal')}
                value={editedInternalNotes}
                onChange={(e) => setEditedInternalNotes(e.target.value)}
                rows={3}
                placeholder={t('customers:placeholders.internalNotes')}
              />
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button variant="secondary" onClick={handleCancelEditNotes} disabled={customerLoading} className="w-full sm:w-auto">
                  {t('customers:notes.cancel')}
                </Button>
                <Button
                  onClick={handleSaveNotes}
                  disabled={customerLoading}
                  leftIcon={<Save className="w-4 h-4" strokeWidth={2} />}
                  className="w-full sm:w-auto"
                >
                  {customerLoading ? t('customers:notes.saving') : t('customers:notes.save')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {editedExternalNotes ? (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
                    {t('customers:notes.external')}
                  </p>
                  <p className="text-[13px] text-slate-700 whitespace-pre-wrap">
                    {editedExternalNotes}
                  </p>
                </div>
              ) : (
                <p className="text-[13px] text-slate-500 italic">
                  {t('customers:notes.noExternal')}
                </p>
              )}

              {editedInternalNotes ? (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
                    {t('customers:notes.internal')}
                  </p>
                  <p className="text-[13px] text-slate-700 whitespace-pre-wrap">
                    {editedInternalNotes}
                  </p>
                </div>
              ) : (
                <p className="text-[13px] text-slate-500 italic">
                  {t('customers:notes.noInternal')}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-3 sm:px-4">
            <TabsList>
              <TabsTrigger value="objects">
                <Building2 className="w-4 h-4 mr-1.5" strokeWidth={2} />
                {t('customers:sections.objects')}
              </TabsTrigger>
              <TabsTrigger value="contacts">
                <Users className="w-4 h-4 mr-1.5" strokeWidth={2} />
                {t('customers:sections.contacts')}
              </TabsTrigger>
              <TabsTrigger value="files">
                <FileText className="w-4 h-4 mr-1.5" strokeWidth={2} />
                {t('customers:sections.files')}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="p-4">
            <TabsContent value="objects" className="pt-0">
              <div className="flex justify-end mb-4">
                <Button
                  onClick={() => openObjectModal()}
                  leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
                  className="w-full sm:w-auto"
                >
                  {t('customers:objects.add')}
                </Button>
              </div>

              {customerLoading ? (
                <Loading />
              ) : objects.length === 0 ? (
                <EmptyState
                  icon={<Building2 className="w-12 h-12" strokeWidth={1.75} />}
                  title={t('customers:objects.empty')}
                  description={t('customers:objects.emptyDescription')}
                />
              ) : (
                <DraggableObjectsTable
                  objects={objects}
                  onReorder={handleReorderObjects}
                  onEdit={openObjectModal}
                  onDelete={openDeleteObjectModal}
                />
              )}
            </TabsContent>

            <TabsContent value="contacts" className="pt-0">
              <div className="flex justify-end mb-4">
                <Button
                  onClick={() => openContactModal()}
                  leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
                  className="w-full sm:w-auto"
                >
                  {t('customers:contacts.add')}
                </Button>
              </div>

              {customerLoading ? (
                <Loading />
              ) : contacts.length === 0 ? (
                <EmptyState
                  icon={<Users className="w-12 h-12" strokeWidth={1.75} />}
                  title={t('customers:contacts.empty')}
                  description={t('customers:contacts.emptyDescription')}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {contacts.map((contact: FacilityFlowCoreModulesCustomersDtosContactDto) => (
                    <div
                      key={contact.id}
                      className="rounded-xl border border-slate-200 bg-white p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight truncate">
                            {formatPersonName(toSalutation(contact.salutation), contact.firstName, contact.lastName, t)}
                          </h3>
                          {contact.role && (
                            <p className="text-[12px] text-slate-500 mt-0.5">
                              {contact.role}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openContactModal(contact)}
                            title={t('customers:contacts.editTitle')}
                            aria-label={t('customers:contacts.editTitle')}
                            className="p-1.5"
                          >
                            <Pencil className="w-4 h-4" strokeWidth={2} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => contact.id && openDeleteContactModal(contact.id)}
                            title={t('customers:contacts.deleteTitle')}
                            aria-label={t('customers:contacts.deleteTitle')}
                            className="p-1.5"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                          </Button>
                        </div>
                      </div>

                      {contact.dateOfBirth && (
                        <div className="flex items-start gap-2">
                          <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <p className="text-[13px] text-slate-700 tabular-nums">
                            {new Date(contact.dateOfBirth).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                            {contact.age !== null && contact.age !== undefined && (
                              <span className="text-slate-500 ml-2">
                                ({contact.age} {t('customers:contacts.age')})
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {contact.emails && contact.emails.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <div className="flex-1 min-w-0">
                            {contact.emails.map((email: string, idx: number) => (
                              <p key={idx} className="text-[13px] text-slate-700 truncate">
                                {email}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {contact.phoneNumbers && contact.phoneNumbers.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <div className="flex-1 min-w-0">
                            {contact.phoneNumbers.map((phone: string, idx: number) => (
                              <p key={idx} className="text-[13px] text-slate-700 tabular-nums truncate">
                                {phone}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {contact.notes && (
                        <p className="text-[12px] text-slate-500 pt-2 border-t border-slate-100 whitespace-pre-wrap">
                          {contact.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="files" className="pt-0">
              {id && <DocumentList ownerType="Customer" ownerId={id} bare />}
            </TabsContent>
          </div>
        </div>
      </Tabs>

      {/* Shared Object Create/Edit SlideOver */}
      {isObjectEditOpen && (
        <ObjectEditSlideOver
          isOpen={isObjectEditOpen}
          onClose={() => {
            setIsObjectEditOpen(false);
            setObjectEditId(null);
          }}
          mode={objectEditMode}
          customerId={objectEditMode === 'create' ? id : undefined}
          object={objectEditMode === 'edit' ? (objectStoreSelectedObject || undefined) : undefined}
          onSave={async (payload) => {
            if (!id) return;

            if (objectEditMode === 'create') {
              await createObject(payload as FacilityFlowCoreModulesCustomersDtosCreateObjectRequest);
            } else if (objectEditId) {
              await updateObject(objectEditId, payload as UpdateObjectRequestWithGeo);
            }

            await getCustomerById(id);
            setIsObjectEditOpen(false);
            setObjectEditId(null);
          }}
        />
      )}

      {/* Contact SlideOver */}
      <SlideOver
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setIsEditingContact(false);
          setSelectedContact(null);
          resetContactForm();
        }}
        title={isEditingContact ? t('customers:contacts.edit') : t('customers:contacts.create')}
        size="xl"
        footer={
          <SlideOverFooter
            onCancel={() => {
              setIsContactModalOpen(false);
              setIsEditingContact(false);
              setSelectedContact(null);
              resetContactForm();
            }}
            onConfirm={isEditingContact ? handleEditContact : handleCreateContact}
            confirmText={isEditingContact ? t('customers:contacts.saveContact') : t('customers:contacts.createContact')}
            loading={contactSaving}
          />
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_1fr] gap-4">
            <SalutationSelect
              value={contactFormData.salutation}
              onChange={(value) => setContactFormData({ ...contactFormData, salutation: value })}
            />
            <FormField
              label={t('customers:contacts.firstName')}
              required
              value={contactFormData.firstName}
              onChange={(e) =>
                setContactFormData({ ...contactFormData, firstName: e.target.value })
              }
            />
            <FormField
              label={t('customers:contacts.lastName')}
              required
              value={contactFormData.lastName}
              onChange={(e) =>
                setContactFormData({ ...contactFormData, lastName: e.target.value })
              }
            />
          </div>
          <FormField
            label={t('customers:contacts.role')}
            value={contactFormData.role}
            onChange={(e) => setContactFormData({ ...contactFormData, role: e.target.value })}
          />
          <FormField
            label={t('customers:contacts.dateOfBirth')}
            type="date"
            value={contactFormData.dateOfBirth}
            onChange={(e) => setContactFormData({ ...contactFormData, dateOfBirth: e.target.value })}
          />
          {contactFormData.emails.map((email, idx) => (
            <FormField
              key={`email-${idx}`}
              label={idx === 0 ? t('customers:contacts.email') : `${t('customers:contacts.email')} ${idx + 1}`}
              type="email"
              value={email}
              onChange={(e) => {
                const newEmails = [...contactFormData.emails];
                newEmails[idx] = e.target.value;
                setContactFormData({ ...contactFormData, emails: newEmails });
              }}
            />
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setContactFormData({ ...contactFormData, emails: [...contactFormData.emails, ''] })
            }
            leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('customers:contacts.addEmail')}
          </Button>

          {contactFormData.phoneNumbers.map((phone, idx) => (
            <FormField
              key={`phone-${idx}`}
              label={idx === 0 ? t('customers:contacts.phone') : `${t('customers:contacts.phone')} ${idx + 1}`}
              type="tel"
              value={phone}
              onChange={(e) => {
                const newPhones = [...contactFormData.phoneNumbers];
                newPhones[idx] = e.target.value;
                setContactFormData({ ...contactFormData, phoneNumbers: newPhones });
              }}
            />
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setContactFormData({
                ...contactFormData,
                phoneNumbers: [...contactFormData.phoneNumbers, ''],
              })
            }
            leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('customers:contacts.addPhone')}
          </Button>

          <TextArea
            label={t('customers:contacts.notes')}
            value={contactFormData.notes}
            onChange={(e) => setContactFormData({ ...contactFormData, notes: e.target.value })}
            rows={3}
          />
        </div>
      </SlideOver>

      {/* Delete Object Confirmation Modal */}
      <Modal
        isOpen={isDeleteObjectModalOpen}
        onClose={() => {
          setIsDeleteObjectModalOpen(false);
          setObjectToDelete(null);
        }}
        title={t('customers:objects.delete')}
        size="sm"
        footer={
          <ModalFooter
            onCancel={() => {
              setIsDeleteObjectModalOpen(false);
              setObjectToDelete(null);
            }}
            onConfirm={handleDeleteObject}
            confirmText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            loading={objectSaving}
            variant="danger"
          />
        }
      >
        <p className="text-[13px] text-slate-700">
          {t('customers:objects.confirmDelete')}
        </p>
      </Modal>

      {/* Delete Contact Confirmation Modal */}
      <Modal
        isOpen={isDeleteContactModalOpen}
        onClose={() => {
          setIsDeleteContactModalOpen(false);
          setContactToDelete(null);
        }}
        title={t('customers:contacts.delete')}
        size="sm"
        footer={
          <ModalFooter
            onCancel={() => {
              setIsDeleteContactModalOpen(false);
              setContactToDelete(null);
            }}
            onConfirm={handleConfirmDeleteContact}
            confirmText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            loading={contactSaving}
            variant="danger"
          />
        }
      >
        <p className="text-[13px] text-slate-700">
          {t('customers:contacts.confirmDelete')}
        </p>
      </Modal>
    </div>
  );
}
